import { useEffect } from "react";

import { isMacOS } from "@/utils/platform";

/**
 * Configuration object for keyboard shortcuts.
 * @interface KeyboardShortcut
 * @property {string} key - The main key to listen for (e.g., 's', 'k', 'Enter')
 * @property {boolean} [ctrl] - Whether Ctrl key (or Cmd on macOS) is required
 * @property {boolean} [shift] - Whether Shift key is required
 * @property {boolean} [alt] - Whether Alt key is required
 */
type KeyboardShortcut = {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
};

/**
 * A custom hook that listens for keyboard shortcuts and executes a callback when triggered.
 * Automatically handles the difference between Ctrl (Windows/Linux) and Cmd (macOS).
 *
 * @param {KeyboardShortcut} shortcut - The keyboard shortcut configuration
 * @param {() => void} callback - The function to execute when the shortcut is triggered
 *
 * @example
 * ```tsx
 * // Simple shortcut: Ctrl+S (Cmd+S on macOS)
 * useKeyboardShortcut(
 *   { key: 's', ctrl: true },
 *   () => saveDocument()
 * );
 *
 * // Complex shortcut: Ctrl+Shift+K
 * useKeyboardShortcut(
 *   { key: 'k', ctrl: true, shift: true },
 *   () => openCommandPalette()
 * );
 *
 * // Alt+Enter shortcut
 * useKeyboardShortcut(
 *   { key: 'Enter', alt: true },
 *   () => handleSpecialSubmit()
 * );
 * ```
 *
 * @remarks
 * - The hook automatically prevents default browser behavior when the shortcut is triggered
 * - Key matching is case-insensitive
 * - On macOS, the 'ctrl' property maps to the Command (⌘) key
 * - The event listener is automatically cleaned up when the component unmounts
 */
export function useKeyboardShortcut(shortcut: KeyboardShortcut, callback: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if event.key is undefined (can happen with browser autocomplete)
      if (!event.key) return;

      const matchesKey = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const isCtrlPressed = isMacOS() ? event.metaKey : event.ctrlKey;
      const matchesCtrl = shortcut.ctrl ? isCtrlPressed : true;
      const matchesShift = shortcut.shift ? event.shiftKey : true;
      const matchesAlt = shortcut.alt ? event.altKey : true;

      if (matchesKey && matchesCtrl && matchesShift && matchesAlt) {
        event.preventDefault();
        callback();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [callback, shortcut]);
}
